// Reference Libraries — seed loader.
//
// READ-ONLY in-memory dataset. NEVER persists to localStorage (per Module 2
// approval: only user records are persisted; seed lives in app code/JSON).
//
// Inputs (both bundled in the app):
//   1. /lib/reference/merged_library_v1.json  — original 89-row seed
//   2. /lib/reflibs/extension_v1.json         — new curated rows closing
//                                                 brief-mandated gaps (DP 2-7/8",
//                                                 HWDP 3-1/2"/4", S-135 6-5/8" DP,
//                                                 casing STC/LTC/BTC/Premium ×
//                                                 J-55/N-80/P-110/Q-125, tubing
//                                                 EUE/NUE × J-55/L-80/N-80/P-110).
//
// Transform pipeline (pure, no I/O):
//   raw row  →  trim+normalise strings  →  v2 field rename  →  withDerived()
//             →  freeze and partition by category

import mergedRaw    from "@/lib/reference/merged_library_v1.json";
import extensionRaw from "./extension_v1.json";
import { withDerived, CATEGORIES } from "./schema";

// merged_library_v1 source uses `heavy_weight_drill_pipe`; we expose it as `hwdp`.
const SOURCE_TYPE_TO_CATEGORY = {
  drill_pipe:               "drill_pipe",
  heavy_weight_drill_pipe:  "hwdp",
  drill_collar:             "drill_collar",
  casing:                   "casing",
  tubing:                   "tubing",
  bit:                      "bit",
  mud_motor:                "mud_motor",
  rotary_steerable:         "rotary_steerable",
  mwd_tool:                 "mwd_tool",
  lwd_tool:                 "lwd_tool",
  stabilizer:               "stabilizer",
  crossover:                "crossover",
};

function _normType(s) {
  if (typeof s !== "string") return "";
  return s.trim().toLowerCase().replace(/\s+/g, "_");
}
function _normGrade(s) {
  if (typeof s !== "string") return s;
  return s.replace(/-\s+/g, "-").trim().replace(/\s+/g, " ");
}
function _trim(s) {
  if (typeof s !== "string") return s;
  return s.trim().replace(/\s+/g, " ");
}

// Map a raw source row → v2 record. Returns null if not a tubular/bit category.
function _adaptRow(raw, sourceLabel) {
  const category = SOURCE_TYPE_TO_CATEGORY[_normType(raw.type)];
  if (!category) return null;

  if (category === "bit") {
    return Object.freeze({
      id: String(raw.id), category: "bit",
      bitSize:      raw.bit_size_in != null ? Number(raw.bit_size_in)
                   : raw.diameter_in != null ? Number(raw.diameter_in) : "",
      bitType:      _trim(raw.bit_type) || _trim(raw.subtype) || "PDC",
      iadcCode:     _trim(raw.iadc_code) || _trim(raw.iadd_code) || "",
      manufacturer: _trim(raw.manufacturer) || "",
      model:        _trim(raw.model) || "",
      nozzles:      raw.nozzle_count != null ? Number(raw.nozzle_count)
                   : raw.num_nozzles != null ? Number(raw.num_nozzles) : "",
      tfa:          raw.tfa_in2 != null ? Number(raw.tfa_in2)
                   : raw.tfa_sqin != null ? Number(raw.tfa_sqin) : "",
      source: sourceLabel, notes: _trim(raw.notes) || "",
      isActive: true, createdAt: null, updatedAt: null,
    });
  }

  // BHA categories — dedicated adapters keep field-mapping localised.
  if (_BHA_ADAPTERS[category]) {
    return _BHA_ADAPTERS[category](raw, sourceLabel);
  }

  // Tubular adapter
  const v2 = withDerived({
    id: String(raw.id), category,
    nominalSize:    _trim(raw.nominal_size) || "",
    outerDiameter:  Number(raw.od_in),
    innerDiameter:  Number(raw.id_in),
    weight:         Number(raw.weight_lbft),
    grade:          _normGrade(raw.grade) || "",
    connection:     _trim(raw.connection_type) || "",
    drift:          raw.drift_diameter_in != null ? Number(raw.drift_diameter_in) : "",
    toolJointOd:    raw.tool_joint_od_in  != null ? Number(raw.tool_joint_od_in)  : "",
    toolJointId:    raw.tool_joint_id_in  != null ? Number(raw.tool_joint_id_in)  : "",
    threadForm:     _trim(raw.thread_form) || "",
    couplingOd:     raw.coupling_od_in    != null ? Number(raw.coupling_od_in)    : "",
    tensileYieldLb:           raw.tensile_yield_lb            != null ? Number(raw.tensile_yield_lb)            : "",
    torsionalYieldFtLb:       raw.torsional_yield_ftlb        != null ? Number(raw.torsional_yield_ftlb)        : "",
    internalYieldPressurePsi: raw.internal_yield_pressure_psi != null ? Number(raw.internal_yield_pressure_psi) : "",
    collapsePressurePsi:      raw.collapse_pressure_psi       != null ? Number(raw.collapse_pressure_psi)       : "",
    minYieldStrengthPsi:      raw.min_yield_strength_psi      != null ? Number(raw.min_yield_strength_psi)      : "",
    source: sourceLabel, notes: _trim(raw.notes) || "",
    isActive: true, createdAt: null, updatedAt: null,
  });
  return Object.freeze(v2);
}

function _adaptBopRow(raw, sourceLabel) {
  // Count ram slots and surface annulars from the source ram[] / annular shape.
  const ramCount = Array.isArray(raw.rams) ? raw.rams.length : "";
  const annularCount = raw.annular ? 1 : 0;
  return Object.freeze({
    id: String(raw.id), category: "bop",
    stackName: _trim(raw.name) || raw.id,
    bopType:   _trim(raw.type) === "subsea" ? "subsea" : "surface",
    nominalSize: raw.bop_size_in != null ? `${raw.bop_size_in} in` : "",
    workingPressure: raw.pressure_rating_psi != null ? Number(raw.pressure_rating_psi) : "",
    ramSlots: ramCount,
    annularCount,
    chokeLineId: raw.choke_line_id_in != null ? Number(raw.choke_line_id_in) : "",
    killLineId:  raw.kill_line_id_in  != null ? Number(raw.kill_line_id_in)  : "",
    accumulatorGal: raw.accumulator_volume_gal != null ? Number(raw.accumulator_volume_gal) : "",
    accumulatorPrechargePsi: raw.accumulator_precharge_psi != null ? Number(raw.accumulator_precharge_psi) : "",
    source: sourceLabel, notes: "",
    isActive: true, createdAt: null, updatedAt: null,
  });
}

function _adaptPumpRow(raw, sourceLabel) {
  // merged_library_v1 source uses `type` + `volumetric_efficiency` (0-1).
  // mud_pumps / extension source uses `pump_type` + `efficiency_pct` (0-100).
  // Handle both shapes here.
  const rawType = String(raw.pump_type || raw.type || "").toLowerCase();
  const pumpType = rawType.includes("duplex") ? "duplex" : "triplex";

  let efficiency = 97;
  if (raw.efficiency_pct != null) {
    efficiency = Number(raw.efficiency_pct);
  } else if (raw.volumetric_efficiency != null) {
    const ve = Number(raw.volumetric_efficiency);
    efficiency = ve <= 1.0 ? ve * 100 : ve;
  }

  return Object.freeze({
    id: String(raw.id), category: "pump",
    pumpModel: _trim(raw.model) || raw.id,
    manufacturer: _trim(raw.manufacturer) || "",
    pumpType,
    linerSize:    raw.liner_size_in    != null ? Number(raw.liner_size_in)    : "",
    strokeLength: raw.stroke_length_in != null ? Number(raw.stroke_length_in) : "",
    rodDiameter:  raw.rod_diameter_in  != null ? Number(raw.rod_diameter_in)  : "",
    efficiency,
    maxPressurePsi: raw.max_pressure_psi != null ? Number(raw.max_pressure_psi) : "",
    source: sourceLabel, notes: _trim(raw.notes) || "",
    isActive: true, createdAt: null, updatedAt: null,
  });
}

// ---- BHA tool adapters ---------------------------------------------------
// Source rows come from `merged_library_v1.equipment[]` whose `type` field is
// untrimmed (e.g. " mud_motor "). Callers pass a row whose `type` has already
// been normalised; the adapters themselves just map field names.

function _adaptMudMotorRow(raw, sourceLabel) {
  return Object.freeze({
    id: String(raw.id), category: "mud_motor",
    model:                   _trim(raw.model) || raw.id,
    manufacturer:            _trim(raw.manufacturer) || "",
    outerDiameter:           raw.od_in != null ? Number(raw.od_in) : "",
    lengthFt:                raw.length_ft != null ? Number(raw.length_ft) : "",
    lobes:                   _trim(raw.lobe_config) || _trim(raw.lobes) || "",
    stages:                  raw.stages != null ? Number(raw.stages) : "",
    bendDegrees:             raw.bend_degrees != null ? Number(raw.bend_degrees) : "",
    maxFlowGpm:              raw.max_flow_gpm != null ? Number(raw.max_flow_gpm) : "",
    maxRpm:                  raw.max_rpm != null ? Number(raw.max_rpm) : "",
    maxTorqueFtLb:           raw.max_torque_ftlb != null ? Number(raw.max_torque_ftlb) : "",
    differentialPressurePsi: raw.differential_pressure_psi != null
                              ? Number(raw.differential_pressure_psi) : "",
    connection:              _trim(raw.connection_type) || "",
    source: sourceLabel, notes: _trim(raw.notes) || "",
    isActive: true, createdAt: null, updatedAt: null,
  });
}

function _adaptRSSRow(raw, sourceLabel) {
  return Object.freeze({
    id: String(raw.id), category: "rotary_steerable",
    model:               _trim(raw.model) || raw.id,
    manufacturer:        _trim(raw.manufacturer) || "",
    outerDiameter:       raw.od_in != null ? Number(raw.od_in) : "",
    lengthFt:            raw.length_ft != null ? Number(raw.length_ft) : "",
    steeringMode:        _trim(raw.steering_mode) || "push-the-bit",
    maxDoglegDeg100ft:   raw.max_dogleg_deg100ft != null ? Number(raw.max_dogleg_deg100ft) : "",
    maxFlowGpm:          raw.max_flow_gpm != null ? Number(raw.max_flow_gpm) : "",
    maxRpm:              raw.max_rpm != null ? Number(raw.max_rpm) : "",
    connection:          _trim(raw.connection_type) || "",
    source: sourceLabel, notes: _trim(raw.notes) || "",
    isActive: true, createdAt: null, updatedAt: null,
  });
}

function _adaptMwdRow(raw, sourceLabel) {
  return Object.freeze({
    id: String(raw.id), category: "mwd_tool",
    model:            _trim(raw.model) || raw.id,
    manufacturer:     _trim(raw.manufacturer) || "",
    outerDiameter:    raw.od_in != null ? Number(raw.od_in) : "",
    innerDiameter:    raw.id_in != null ? Number(raw.id_in) : "",
    lengthFt:         raw.length_ft != null ? Number(raw.length_ft) : "",
    telemetryType:    _trim(raw.telemetry_type) || "pulse",
    maxFlowGpm:       raw.max_flow_gpm != null ? Number(raw.max_flow_gpm) : "",
    maxPressurePsi:   raw.max_pressure_psi != null ? Number(raw.max_pressure_psi) : "",
    connection:       _trim(raw.connection_type) || "",
    source: sourceLabel, notes: _trim(raw.notes) || "",
    isActive: true, createdAt: null, updatedAt: null,
  });
}

function _adaptLwdRow(raw, sourceLabel) {
  const sensors = Array.isArray(raw.sensors) ? raw.sensors.join(", ")
                : _trim(raw.sensors) || "";
  return Object.freeze({
    id: String(raw.id), category: "lwd_tool",
    model:            _trim(raw.model) || raw.id,
    manufacturer:     _trim(raw.manufacturer) || "",
    outerDiameter:    raw.od_in != null ? Number(raw.od_in) : "",
    innerDiameter:    raw.id_in != null ? Number(raw.id_in) : "",
    lengthFt:         raw.length_ft != null ? Number(raw.length_ft) : "",
    sensors,
    maxFlowGpm:       raw.max_flow_gpm != null ? Number(raw.max_flow_gpm) : "",
    maxPressurePsi:   raw.max_pressure_psi != null ? Number(raw.max_pressure_psi) : "",
    connection:       _trim(raw.connection_type) || "",
    source: sourceLabel, notes: _trim(raw.notes) || "",
    isActive: true, createdAt: null, updatedAt: null,
  });
}

function _adaptStabilizerRow(raw, sourceLabel) {
  return Object.freeze({
    id: String(raw.id), category: "stabilizer",
    model:            _trim(raw.model) || _trim(raw.name) || raw.id,
    manufacturer:     _trim(raw.manufacturer) || "",
    outerDiameter:    raw.od_in != null ? Number(raw.od_in) : "",
    lengthFt:         raw.length_ft != null ? Number(raw.length_ft) : "",
    bladeType:        _trim(raw.blade_type) || "integral",
    blades:           raw.blades != null ? Number(raw.blades) : "",
    throughPipeId:    raw.through_pipe_id_in != null ? Number(raw.through_pipe_id_in) : "",
    connection:       _trim(raw.connection_type) || "",
    source: sourceLabel, notes: _trim(raw.notes) || "",
    isActive: true, createdAt: null, updatedAt: null,
  });
}

function _adaptCrossoverRow(raw, sourceLabel) {
  const stepped = Boolean(raw.stepped) ||
                  (raw.od1_in != null && raw.od2_in != null);
  return Object.freeze({
    id: String(raw.id), category: "crossover",
    model:             _trim(raw.model) || _trim(raw.name) || raw.id,
    topConnection:     _trim(raw.top_connection) || "",
    bottomConnection:  _trim(raw.bottom_connection) || "",
    stepped,
    outerDiameter:     raw.od_in != null ? Number(raw.od_in) : "",
    innerDiameter:     raw.id_in != null ? Number(raw.id_in) : "",
    lengthIn:          raw.length_in != null ? Number(raw.length_in) : "",
    od1:               raw.od1_in   != null ? Number(raw.od1_in)   : "",
    len1:              raw.len1_in  != null ? Number(raw.len1_in)  : "",
    od2:               raw.od2_in   != null ? Number(raw.od2_in)   : "",
    len2:              raw.len2_in  != null ? Number(raw.len2_in)  : "",
    source: sourceLabel, notes: _trim(raw.notes) || "",
    isActive: true, createdAt: null, updatedAt: null,
  });
}

const _BHA_ADAPTERS = {
  mud_motor:        _adaptMudMotorRow,
  rotary_steerable: _adaptRSSRow,
  mwd_tool:         _adaptMwdRow,
  lwd_tool:         _adaptLwdRow,
  stabilizer:       _adaptStabilizerRow,
  crossover:        _adaptCrossoverRow,
};

let _cache = null;

/**
 * Returns the frozen seed dataset, partitioned by category.
 * Shape: { drill_pipe: [...], hwdp: [...], drill_collar: [...], casing: [...], tubing: [...] }
 * Pure & memoised — safe to call repeatedly.
 */
export function getSeedByCategory() {
  if (_cache) return _cache;
  const buckets = Object.fromEntries(CATEGORIES.map((c) => [c, []]));
  const seen = new Set();   // dedupe by id

  // 1) merged_library_v1 — tubular categories (`equipment[]`) + BOP stacks
  //    (`bop_stacks[]`) + mud pumps (`mud_pumps[]`).
  const mergedItems = Array.isArray(mergedRaw.equipment) ? mergedRaw.equipment : [];
  for (const raw of mergedItems) {
    const v2 = _adaptRow(raw, "rsc_seed_v1");
    if (!v2) continue;
    if (seen.has(v2.id)) continue;
    seen.add(v2.id);
    buckets[v2.category].push(v2);
  }
  const mergedBops = Array.isArray(mergedRaw.bop_stacks) ? mergedRaw.bop_stacks : [];
  for (const raw of mergedBops) {
    const v2 = _adaptBopRow(raw, "rsc_seed_v1");
    if (seen.has(v2.id)) continue;
    seen.add(v2.id);
    buckets.bop.push(v2);
  }
  const mergedPumps = [
    ...(Array.isArray(mergedRaw.mud_pumps) ? mergedRaw.mud_pumps : []),
    ...(Array.isArray(mergedRaw.pumps)     ? mergedRaw.pumps     : []),
  ];
  for (const raw of mergedPumps) {
    const v2 = _adaptPumpRow(raw, "rsc_seed_v1");
    if (seen.has(v2.id)) continue;
    seen.add(v2.id);
    buckets.pump.push(v2);
  }

  // 2) extension_v1 — tubular extension rows + any future bop/pump items
  const extItems = Array.isArray(extensionRaw.items) ? extensionRaw.items : [];
  for (const raw of extItems) {
    const v2 = _adaptRow(raw, "rsc_seed_v1");
    if (!v2) continue;
    if (seen.has(v2.id)) continue;
    seen.add(v2.id);
    buckets[v2.category].push(v2);
  }
  const extBops = Array.isArray(extensionRaw.bop_stacks) ? extensionRaw.bop_stacks : [];
  for (const raw of extBops) {
    const v2 = _adaptBopRow(raw, "rsc_seed_v1");
    if (seen.has(v2.id)) continue;
    seen.add(v2.id);
    buckets.bop.push(v2);
  }
  const extPumps = Array.isArray(extensionRaw.mud_pumps) ? extensionRaw.mud_pumps : [];
  for (const raw of extPumps) {
    const v2 = _adaptPumpRow(raw, "rsc_seed_v1");
    if (seen.has(v2.id)) continue;
    seen.add(v2.id);
    buckets.pump.push(v2);
  }

  // Stable sort: nominal size → grade → connection (tubular); for BOP/Bit/Pump
  // use sensible category-specific keys.
  for (const c of CATEGORIES) {
    buckets[c].sort((a, b) => {
      if (c === "bop") {
        if (a.bopType !== b.bopType) return a.bopType === "surface" ? -1 : 1;
        if (a.workingPressure !== b.workingPressure) return Number(a.workingPressure) - Number(b.workingPressure);
        return String(a.nominalSize).localeCompare(String(b.nominalSize), undefined, { numeric: true });
      }
      if (c === "bit") {
        const aS = Number(a.bitSize) || 0, bS = Number(b.bitSize) || 0;
        if (aS !== bS) return aS - bS;
        return String(a.bitType).localeCompare(String(b.bitType));
      }
      if (c === "pump") {
        return String(a.pumpModel).localeCompare(String(b.pumpModel));
      }
      if (["mud_motor","rotary_steerable","mwd_tool","lwd_tool","stabilizer"].includes(c)) {
        const ao = Number(a.outerDiameter) || 0, bo = Number(b.outerDiameter) || 0;
        if (ao !== bo) return ao - bo;
        return String(a.model || "").localeCompare(String(b.model || ""));
      }
      if (c === "crossover") {
        const at = String(a.topConnection || ""), bt = String(b.topConnection || "");
        if (at !== bt) return at.localeCompare(bt);
        return String(a.bottomConnection || "").localeCompare(String(b.bottomConnection || ""));
      }
      const an = String(a.nominalSize), bn = String(b.nominalSize);
      if (an !== bn) return an.localeCompare(bn, undefined, { numeric: true });
      const ag = String(a.grade), bg = String(b.grade);
      if (ag !== bg) return ag.localeCompare(bg);
      return String(a.connection).localeCompare(String(b.connection));
    });
    Object.freeze(buckets[c]);
  }
  _cache = Object.freeze(buckets);
  return _cache;
}

/** Flat list of all seed records, all categories. */
export function getSeedAll() {
  const by = getSeedByCategory();
  return CATEGORIES.flatMap((c) => by[c]);
}

/** Stats — handy for diagnostics. Returns { totalRows, byCategory:{...} }. */
export function getSeedStats() {
  const by = getSeedByCategory();
  const byCategory = {};
  for (const c of CATEGORIES) byCategory[c] = by[c].length;
  return { totalRows: getSeedAll().length, byCategory };
}
